﻿import { useEffect, useRef } from 'react'
import {
  Map as MlMap,
  Marker,
  Popup,
  NavigationControl,
  GeolocateControl,
  ScaleControl,
  addProtocol,
  type GeoJSONSource,
  type MapMouseEvent,
  type StyleSpecification,
  type LayerSpecification,
} from 'maplibre-gl'
import mlcontour from 'maplibre-contour'
import type { Feature, FeatureCollection, Point } from 'geojson'
import { MAP_STYLE_URL, TERRAIN_TILES } from '../lib/providers'
import { CACHED_PROTOCOL } from '../lib/offline'
import { friendlyGeoError } from '../lib/platform'
import { alongLine, boundsOf, nearestPointOnLine, type LngLat, type LngLatEle } from '../lib/geo'
import { fetchPois, POI_CATEGORIES, type Poi } from '../lib/pois'
import { usePlanner } from '../state/planner'
import { useUi, type MapLayer } from '../state/ui'
import { useNav } from '../state/nav'
import { useRecorder } from '../state/recorder'
import { useTours } from '../state/tours'

const POI_COLORS: Record<string, string> = {
  peak: '#845ef7',
  viewpoint: '#f76707',
  hut: '#e8590c',
  water: '#1971c2',
  parking: '#495057',
  camping: '#2f9e44',
  restaurant: '#d6336c',
}

/**
 * Liberty ships OSM-bright colors (yellow roads, orange casings, violet water).
 * Re-tint to a calm, warm-neutral outdoor palette. Keys are compared with
 * whitespace stripped + lowercased.
 */
const PALETTE: Record<string, string> = {
  '#f8f4f0': '#f1efe9', // background → warm paper
  '#fc8': '#f2cd9d', // motorway → muted apricot
  '#fea': '#fbf3e0', // primary/secondary → soft ivory
  '#fff4c6': '#f7f1e1', // tunnel majors
  '#ffdaa6': '#eed7b2', // tunnel motorway
  '#e9ac77': '#d5c1a1', // orange casings → warm taupe
  'rgb(158,189,255)': '#a2c4d9', // water → steel blue
  '#a0c8f0': '#a2c4d9', // waterways
  '#d8e8c8': '#d3e0c0', // park → sage
  'hsla(98,61%,72%,0.7)': 'hsla(104,30%,70%,0.6)', // wood
  'rgba(176,213,154,1)': '#c2d5ab', // grass
  'rgba(247,239,195,1)': '#eee7cd', // sand
  'rgb(236,238,204)': '#e9e8d6', // school
  '#fde': '#f2e7e2', // hospital
  '#dee3cd': '#dde1cf', // pitch/track
  'hsl(35,8%,85%)': '#e6e2d8', // buildings → lighter warm grey
  'hsl(35,6%,79%)': '#d9d4c7', // building outlines
  'hsla(35,6%,79%,0.32)': 'hsla(38,12%,78%,0.32)',
}

const paletteKey = (v: string) => v.replace(/\s+/g, '').toLowerCase()

/** client-side contour tiles generated from the same terrarium DEM */
const demSource = new mlcontour.DemSource({ url: TERRAIN_TILES, encoding: 'terrarium', maxzoom: 12, worker: true })
demSource.setupMaplibre({ addProtocol })

function retint(value: unknown): unknown {
  if (typeof value === 'string') return PALETTE[paletteKey(value)] ?? value
  if (Array.isArray(value)) return value.map(retint)
  return value
}

/** Fetch the base style, inline TileJSON and reroute tiles through cached:// */
async function buildStyle(): Promise<StyleSpecification> {
  const style = (await (await fetch(MAP_STYLE_URL)).json()) as StyleSpecification
  for (const layer of style.layers) {
    if ('paint' in layer && layer.paint) {
      const paint = layer.paint as Record<string, unknown>
      for (const k of Object.keys(paint)) paint[k] = retint(paint[k])
    }
  }
  for (const src of Object.values(style.sources)) {
    if (src.type === 'vector' && !src.tiles && src.url) {
      const tj = (await (await fetch(src.url)).json()) as { tiles?: string[]; minzoom?: number; maxzoom?: number }
      if (tj.tiles) {
        src.tiles = tj.tiles
        src.minzoom = tj.minzoom
        src.maxzoom = tj.maxzoom
        delete src.url
      }
    }
    if (src.type === 'vector' && src.tiles) {
      src.tiles = src.tiles.map((t) => `${CACHED_PROTOCOL}://${t}`)
    }
  }
  style.sources['dem'] = {
    type: 'raster-dem',
    tiles: [TERRAIN_TILES],
    encoding: 'terrarium',
    tileSize: 256,
    maxzoom: 15,
    attribution: '',
  }
  style.sources['contours'] = {
    type: 'vector',
    tiles: [
      demSource.contourProtocolUrl({
        multiplier: 1,
        thresholds: {
          10: [500, 2500],
          11: [200, 1000],
          12: [100, 500],
          13: [100, 500],
          14: [50, 200],
          15: [20, 100],
        },
        contourLayer: 'contours',
        elevationKey: 'ele',
        levelKey: 'level',
      }),
    ],
    maxzoom: 15,
  }

  const firstSymbol = style.layers.find((l) => l.type === 'symbol')?.id
  const hillshade: LayerSpecification = {
    id: 'trv-hillshade',
    type: 'hillshade',
    source: 'dem',
    paint: {
      'hillshade-exaggeration': 0.3,
      'hillshade-shadow-color': '#56614e',
      'hillshade-highlight-color': '#fdfdf6',
      'hillshade-accent-color': '#8a9b7e',
    },
  }
  const contourLines: LayerSpecification = {
    id: 'trv-contours',
    type: 'line',
    source: 'contours',
    'source-layer': 'contours',
    minzoom: 10.5,
    layout: { 'line-join': 'round' },
    paint: {
      'line-color': 'rgba(112, 102, 80, 0.5)',
      'line-opacity': ['interpolate', ['linear'], ['zoom'], 10.5, 0, 11.5, 0.55],
      'line-width': ['case', ['>=', ['get', 'level'], 1], 1, 0.45],
    },
  }
  const contourLabels: LayerSpecification = {
    id: 'trv-contour-labels',
    type: 'symbol',
    source: 'contours',
    'source-layer': 'contours',
    minzoom: 12,
    filter: ['>=', ['get', 'level'], 1],
    layout: {
      'symbol-placement': 'line',
      'text-field': ['concat', ['to-string', ['get', 'ele']], ' m'],
      'text-font': ['Noto Sans Regular'],
      'text-size': 9.5,
      'symbol-spacing': 320,
    },
    paint: {
      'text-color': '#77705c',
      'text-halo-color': 'rgba(241, 239, 233, 0.9)',
      'text-halo-width': 1.3,
    },
  }
  const idx = firstSymbol ? style.layers.findIndex((l) => l.id === firstSymbol) : style.layers.length
  style.layers.splice(idx, 0, hillshade, contourLines, contourLabels)
  return style
}

const EMPTY_FC: FeatureCollection = { type: 'FeatureCollection', features: [] }
const lineFC = (coords: readonly (LngLat | LngLatEle)[]): Feature => ({
  type: 'Feature',
  properties: {},
  geometry: { type: 'LineString', coordinates: coords.map((c) => [...c]) },
})

export default function MapView() {
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<MlMap | null>(null)
  const readyRef = useRef(false)
  const wpMarkersRef = useRef<Marker[]>([])
  const posMarkerRef = useRef<Marker | null>(null)
  const hoverMarkerRef = useRef<Marker | null>(null)
  const baseLayerIdsRef = useRef<string[]>([])
  const lastFitKeyRef = useRef('')
  const popupRef = useRef<Popup | null>(null)

  // ---- init ----
  useEffect(() => {
    let disposed = false
    // restore the last viewed area (no location prompt on load — iOS punishes those)
    let center: LngLat = [11.4, 47.6]
    let zoom = 9
    try {
      const saved = JSON.parse(localStorage.getItem('traverse.view') ?? 'null') as
        | { c: LngLat; z: number }
        | null
      if (saved && Number.isFinite(saved.z)) {
        center = saved.c
        zoom = saved.z
      }
    } catch {
      /* corrupt state — use default */
    }
    // data credits live in Profile → About instead of a floating control
    const map = new MlMap({
      container: containerRef.current!,
      style: { version: 8, sources: {}, layers: [] },
      center,
      zoom,
      attributionControl: false,
      maxPitch: 70,
    })
    mapRef.current = map
    map.on('error', (e) => console.error('[map]', e.error?.message ?? e))
    ;(window as unknown as { __map?: MlMap }).__map = map
    map.addControl(new NavigationControl({ showCompass: true }), 'top-right')
    const geolocate = new GeolocateControl({
      positionOptions: { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 },
      trackUserLocation: true,
    })
    geolocate.on('error', (e) => useUi.getState().showToast(friendlyGeoError(e?.code)))
    map.addControl(geolocate, 'top-right')
    map.addControl(new ScaleControl({ maxWidth: 90 }), 'bottom-right')

    void buildStyle().then((style) => {
      if (disposed) return
      map.setStyle(style)
      map.once('styledata', () => {
        baseLayerIdsRef.current = style.layers.map((l) => l.id)
        addAppLayers(map)
        readyRef.current = true
        syncAll()
      })
    })

    map.on('click', onMapClick)

    // POIs on move end
    let poiTimer: ReturnType<typeof setTimeout> | undefined
    const loadPois = () => {
      clearTimeout(poiTimer)
      poiTimer = setTimeout(async () => {
        if (!readyRef.current) return
        const cats = useUi.getState().poiCategories
        const src = map.getSource('pois') as GeoJSONSource | undefined
        if (!src) return
        if (!cats.length || map.getZoom() < 10.5) {
          src.setData(EMPTY_FC)
          return
        }
        const b = map.getBounds()
        const pois = await fetchPois([b.getSouth(), b.getWest(), b.getNorth(), b.getEast()], cats).catch(() => [])
        src.setData({
          type: 'FeatureCollection',
          features: pois.map((p) => ({
            type: 'Feature',
            properties: { name: p.name, category: p.category, ele: p.tags.ele ?? '' },
            geometry: { type: 'Point', coordinates: [p.lng, p.lat] },
          })),
        })
      }, 450)
    }
    map.on('moveend', loadPois)
    const unsubPoi = useUi.subscribe((s, prev) => {
      if (s.poiCategories !== prev.poiCategories) loadPois()
    })

    // remember where the user was looking (throttled via moveend)
    const saveView = () => {
      const c = map.getCenter()
      localStorage.setItem(
        'traverse.view',
        JSON.stringify({ c: [Math.round(c.lng * 1e5) / 1e5, Math.round(c.lat * 1e5) / 1e5], z: Math.round(map.getZoom() * 10) / 10 }),
      )
    }
    map.on('moveend', saveView)

    return () => {
      disposed = true
      unsubPoi()
      map.remove()
      mapRef.current = null
      readyRef.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function addAppLayers(map: MlMap) {
    map.addSource('route', { type: 'geojson', data: EMPTY_FC })
    map.addSource('route-progress', { type: 'geojson', data: EMPTY_FC })
    map.addSource('track', { type: 'geojson', data: EMPTY_FC })
    map.addSource('tour', { type: 'geojson', data: EMPTY_FC })
    map.addSource('pois', { type: 'geojson', data: EMPTY_FC })

    map.addLayer({
      id: 'route-casing',
      type: 'line',
      source: 'route',
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': '#ffffff', 'line-width': 9, 'line-opacity': 0.9 },
    })
    map.addLayer({
      id: 'route-line',
      type: 'line',
      source: 'route',
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': '#149457', 'line-width': 5 },
    })
    map.addLayer({
      id: 'route-progress-line',
      type: 'line',
      source: 'route-progress',
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': '#14663a', 'line-width': 7 },
    })
    map.addLayer({
      id: 'tour-line',
      type: 'line',
      source: 'tour',
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': '#7048e8', 'line-width': 4.5, 'line-opacity': 0.9 },
    })
    map.addLayer({
      id: 'track-line',
      type: 'line',
      source: 'track',
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': '#e8590c', 'line-width': 5 },
    })
    map.addLayer({
      id: 'pois-circles',
      type: 'circle',
      source: 'pois',
      paint: {
        'circle-radius': 6.5,
        'circle-color': [
          'match',
          ['get', 'category'],
          ...Object.entries(POI_COLORS).flat(),
          '#666',
        ] as unknown as string,
        'circle-stroke-width': 2,
        'circle-stroke-color': '#fff',
      },
    })
    map.addLayer({
      id: 'pois-labels',
      type: 'symbol',
      source: 'pois',
      minzoom: 12.5,
      layout: {
        'text-field': ['get', 'name'],
        'text-font': ['Noto Sans Regular'],
        'text-size': 11,
        'text-offset': [0, 1.1],
        'text-anchor': 'top',
        'text-optional': true,
      },
      paint: { 'text-color': '#333d36', 'text-halo-color': '#fff', 'text-halo-width': 1.4 },
    })

    // raster overlays for topo / satellite, hidden by default
    map.addSource('topo-raster', {
      type: 'raster',
      tiles: [
        'https://a.tile.opentopomap.org/{z}/{x}/{y}.png',
        'https://b.tile.opentopomap.org/{z}/{x}/{y}.png',
        'https://c.tile.opentopomap.org/{z}/{x}/{y}.png',
      ],
      tileSize: 256,
      maxzoom: 17,
      attribution: 'Â© OpenTopoMap (CC-BY-SA)',
    })
    map.addSource('sat-raster', {
      type: 'raster',
      tiles: [
        'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      ],
      tileSize: 256,
      maxzoom: 19,
      attribution: 'Imagery Â© Esri, Maxar, Earthstar Geographics',
    })
    const firstId = map.getStyle().layers[0]?.id
    map.addLayer(
      { id: 'topo-raster-layer', type: 'raster', source: 'topo-raster', layout: { visibility: 'none' } },
      firstId,
    )
    map.addLayer(
      { id: 'sat-raster-layer', type: 'raster', source: 'sat-raster', layout: { visibility: 'none' } },
      firstId,
    )

    map.on('mouseenter', 'route-line', () => (map.getCanvas().style.cursor = 'pointer'))
    map.on('mouseleave', 'route-line', () => (map.getCanvas().style.cursor = ''))
    map.on('mouseenter', 'pois-circles', () => (map.getCanvas().style.cursor = 'pointer'))
    map.on('mouseleave', 'pois-circles', () => (map.getCanvas().style.cursor = ''))
  }

  function onMapClick(e: MapMouseEvent) {
    const map = mapRef.current
    if (!map || !readyRef.current) return
    if (useNav.getState().active || useRecorder.getState().status !== 'idle') return

    const poiHit = map.queryRenderedFeatures(e.point, { layers: ['pois-circles'] })[0]
    if (poiHit) {
      const [lng, lat] = (poiHit.geometry as Point).coordinates
      const { name, category, ele } = poiHit.properties as { name: string; category: Poi['category']; ele: string }
      const catInfo = POI_CATEGORIES.find((c) => c.id === category)
      const el = document.createElement('div')
      el.className = 'poi-pop'
      el.innerHTML = `<div class="p-name">${escapeHtml(name)}</div><div class="p-kind">${catInfo?.icon ?? ''} ${
        catInfo?.label ?? ''
      }${ele ? ` Â· ${escapeHtml(ele)} m` : ''}</div>`
      const btn = document.createElement('button')
      btn.className = 'btn sm primary'
      btn.textContent = 'Add to route'
      btn.onclick = () => {
        usePlanner.getState().addWaypoint([lng, lat], name)
        popupRef.current?.remove()
      }
      el.appendChild(btn)
      popupRef.current?.remove()
      popupRef.current = new Popup({ offset: 10 }).setLngLat([lng, lat]).setDOMContent(el).addTo(map)
      return
    }

    if (useUi.getState().panel !== 'plan') return
    const planner = usePlanner.getState()
    const pt: LngLat = [e.lngLat.lng, e.lngLat.lat]

    const routeHit = map.queryRenderedFeatures(e.point, { layers: ['route-line'] })[0]
    if (routeHit && planner.route && planner.waypoints.length >= 2) {
      // insert a via point between the correct pair of waypoints
      const clicked = nearestPointOnLine(planner.route.coords, pt)
      const wpAlong = planner.waypoints.map(
        (w) => nearestPointOnLine(planner.route!.coords, [w.lng, w.lat]).alongM,
      )
      let idx = wpAlong.findIndex((m) => m > clicked.alongM)
      if (idx === -1) idx = planner.waypoints.length - 1
      planner.insertWaypoint(Math.max(1, idx), clicked.point)
      return
    }
    planner.addWaypoint(pt)
  }

  // ---- sync store â†’ map ----
  function syncAll() {
    syncRoute()
    syncWaypoints()
    syncLayerChoice()
  }

  const syncRoute = () => {
    const map = mapRef.current
    if (!map || !readyRef.current) return
    const { route, waypoints, sport } = usePlanner.getState()
    const src = map.getSource('route') as GeoJSONSource | undefined
    if (!src) return
    src.setData(route ? { type: 'FeatureCollection', features: [lineFC(route.coords)] } : EMPTY_FC)
    if (route) {
      const fitKey = `${waypoints.length}|${sport}|${waypoints
        .map((w) => `${w.lng.toFixed(4)},${w.lat.toFixed(4)}`)
        .join(';')}`
      if (fitKey !== lastFitKeyRef.current) {
        lastFitKeyRef.current = fitKey
        const [[minX, minY], [maxX, maxY]] = boundsOf(route.coords)
        map.fitBounds(
          [
            [minX, minY],
            [maxX, maxY],
          ],
          { padding: fitPadding(), maxZoom: 15, duration: 500 },
        )
      }
    }
  }

  function fitPadding() {
    const mobile = window.innerWidth < 900
    return mobile
      ? { top: 70, bottom: Math.round(window.innerHeight * 0.42), left: 40, right: 40 }
      : { top: 70, bottom: 70, left: 440, right: 60 }
  }

  const syncWaypoints = () => {
    const map = mapRef.current
    if (!map || !readyRef.current) return
    const { waypoints } = usePlanner.getState()
    for (const m of wpMarkersRef.current) m.remove()
    wpMarkersRef.current = waypoints.map((w, i) => {
      const el = document.createElement('div')
      el.className = `wp-marker${i === 0 ? ' first' : ''}${i === waypoints.length - 1 && i > 0 ? ' last' : ''}`
      el.textContent = String(i + 1)
      el.title = 'drag to move Â· double-click to remove'
      el.addEventListener('dblclick', (ev) => {
        ev.stopPropagation()
        usePlanner.getState().removeWaypoint(i)
      })
      const marker = new Marker({ element: el, draggable: true }).setLngLat([w.lng, w.lat]).addTo(map)
      marker.on('drag', () => {
        const p = marker.getLngLat()
        usePlanner.getState().moveWaypoint(i, [p.lng, p.lat])
      })
      marker.on('dragend', () => usePlanner.getState().finishMove(i))
      return marker
    })
  }

  const syncLayerChoice = () => {
    const map = mapRef.current
    if (!map || !readyRef.current) return
    const { layer, terrain3d } = useUi.getState()
    const showBase = layer === 'outdoor'
    for (const id of baseLayerIdsRef.current) {
      if (!map.getLayer(id)) continue
      // terrain decorations (hillshade/contours) belong to the outdoor style only
      if (id.startsWith('trv-')) map.setLayoutProperty(id, 'visibility', showBase ? 'visible' : 'none')
      else map.setLayoutProperty(id, 'visibility', showBase || layer === 'satellite' ? adjustForSat(id, layer) : 'none')
    }
    map.setLayoutProperty('topo-raster-layer', 'visibility', layer === 'topo' ? 'visible' : 'none')
    map.setLayoutProperty('sat-raster-layer', 'visibility', layer === 'satellite' ? 'visible' : 'none')
    map.setTerrain(terrain3d ? { source: 'dem', exaggeration: 1.25 } : null)
    if (terrain3d && map.getPitch() < 20) map.easeTo({ pitch: 55, duration: 600 })
    if (!terrain3d && map.getPitch() > 0) map.easeTo({ pitch: 0, duration: 400 })
  }

  /** over satellite imagery keep only labels/roads from the vector style */
  function adjustForSat(id: string, layer: MapLayer): 'visible' | 'none' {
    if (layer !== 'satellite') return 'visible'
    const map = mapRef.current!
    const type = map.getLayer(id)?.type
    return type === 'symbol' ? 'visible' : 'none'
  }

  useEffect(() => usePlanner.subscribe((s, p) => {
    if (s.route !== p.route) syncRoute()
    if (s.waypoints !== p.waypoints) {
      syncWaypoints()
      // first waypoint set from search / my-location → bring the map there
      if (s.waypoints.length === 1 && p.waypoints.length === 0) {
        const w = s.waypoints[0]
        const map = mapRef.current
        if (map) map.easeTo({ center: [w.lng, w.lat], zoom: Math.max(map.getZoom(), 13), duration: 700 })
      }
    }
  }), [])

  useEffect(() => useUi.subscribe((s, p) => {
    if (s.layer !== p.layer || s.terrain3d !== p.terrain3d) syncLayerChoice()
  }), [])

  // elevation-chart hover marker
  useEffect(() => usePlanner.subscribe((s, p) => {
    if (s.hoverAtM === p.hoverAtM) return
    const map = mapRef.current
    if (!map || !readyRef.current) return
    if (s.hoverAtM == null || !s.route) {
      hoverMarkerRef.current?.remove()
      hoverMarkerRef.current = null
      return
    }
    const pos = alongLine(s.route.coords, s.hoverAtM)
    if (!hoverMarkerRef.current) {
      const el = document.createElement('div')
      el.className = 'pos-marker'
      el.style.background = '#e8590c'
      hoverMarkerRef.current = new Marker({ element: el }).setLngLat(pos).addTo(map)
    } else hoverMarkerRef.current.setLngLat(pos)
  }), [])

  // recorded track + live position
  useEffect(() => useRecorder.subscribe((s, p) => {
    const map = mapRef.current
    if (!map || !readyRef.current) return
    if (s.points !== p.points) {
      const src = map.getSource('track') as GeoJSONSource | undefined
      src?.setData(s.points.length > 1 ? { type: 'FeatureCollection', features: [lineFC(s.points)] } : EMPTY_FC)
    }
    if (s.lastFix && s.lastFix !== p.lastFix) {
      updatePosMarker([s.lastFix.coords.longitude, s.lastFix.coords.latitude])
      if (s.status === 'recording') map.easeTo({ center: [s.lastFix.coords.longitude, s.lastFix.coords.latitude], duration: 800 })
    }
    if (s.status === 'idle' && p.status !== 'idle') {
      posMarkerRef.current?.remove()
      posMarkerRef.current = null
      ;(map.getSource('track') as GeoJSONSource | undefined)?.setData(EMPTY_FC)
    }
  }), [])

  // navigation: route line, progress, camera follow
  useEffect(() => useNav.subscribe((s, p) => {
    const map = mapRef.current
    if (!map || !readyRef.current) return
    const routeSrc = map.getSource('route') as GeoJSONSource | undefined
    const progSrc = map.getSource('route-progress') as GeoJSONSource | undefined
    if (s.active && (s.coords !== p.coords || !p.active)) {
      routeSrc?.setData({ type: 'FeatureCollection', features: [lineFC(s.coords)] })
    }
    if (s.active && s.position && s.position !== p.position) {
      const here: LngLat = [s.position.coords.longitude, s.position.coords.latitude]
      updatePosMarker(here)
      map.easeTo({ center: here, zoom: Math.max(map.getZoom(), 14.5), duration: 800 })
      // progress line: everything behind the current position
      const behind: LngLat[] = []
      let acc = 0
      for (let i = 0; i < s.coords.length - 1 && acc < s.alongM; i++) {
        behind.push([s.coords[i][0], s.coords[i][1]])
        acc += haversineApprox(s.coords[i], s.coords[i + 1])
      }
      behind.push(alongLine(s.coords, s.alongM))
      progSrc?.setData(behind.length > 1 ? { type: 'FeatureCollection', features: [lineFC(behind)] } : EMPTY_FC)
    }
    if (!s.active && p.active) {
      progSrc?.setData(EMPTY_FC)
      posMarkerRef.current?.remove()
      posMarkerRef.current = null
      syncRoute()
    }
  }), [])

  // opened tour geometry
  useEffect(() => useTours.subscribe((s, p) => {
    const map = mapRef.current
    if (!map || !readyRef.current || s.openId === p.openId) return
    const src = map.getSource('tour') as GeoJSONSource | undefined
    const tour = s.tours.find((t) => t.id === s.openId)
    if (!tour) {
      src?.setData(EMPTY_FC)
      return
    }
    src?.setData({ type: 'FeatureCollection', features: [lineFC(tour.data.coords)] })
    const [[minX, minY], [maxX, maxY]] = boundsOf(tour.data.coords)
    map.fitBounds(
      [
        [minX, minY],
        [maxX, maxY],
      ],
      { padding: fitPadding(), maxZoom: 15, duration: 500 },
    )
  }), [])

  function updatePosMarker(pos: LngLat) {
    const map = mapRef.current!
    if (!posMarkerRef.current) {
      const el = document.createElement('div')
      el.className = 'pos-marker'
      posMarkerRef.current = new Marker({ element: el }).setLngLat(pos).addTo(map)
    } else posMarkerRef.current.setLngLat(pos)
  }

  return <div ref={containerRef} className="map-root" />
}

function haversineApprox(a: LngLatEle, b: LngLatEle): number {
  const dx = (b[0] - a[0]) * 111320 * Math.cos((a[1] * Math.PI) / 180)
  const dy = (b[1] - a[1]) * 111320
  return Math.hypot(dx, dy)
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}
